<template>
  <div class="utleier">
    <div  class="mt-3 text-center">
        <div class="small-container">
          <!-- Private Landlord -->
          <div v-if="signup.landlord.type === 'private'" class="mb-5">
            <hl-H1 class="text-left">Fortell oss mer om deg som utleier, {{signup.user.fullName}}</hl-H1>
            <p class="text-left">For å opprette en <b>utleierprofil</b> må du oppgi noen flere opplysninger om deg som privatperson. Om du leier ut via en virksomhet så kan du gå tilbake til forrige side og velge “Jeg leier ut via en virksomhet”</p>
          </div>
          <!-- / Private Landlord -->

          <!-- Company Landlord -->
          <div v-else class="mb-5">
            <h1 class="text-left">Fortell oss mer om {{ companyNameOrPlaceholder }}, {{ signup.user.fullName }}</h1>
            <p class="text-left">For å opprette en <b>utleierprofil</b> må du oppgi noen flere opplysninger om virksomheten du leier ut via. Har du flere virksomheter du leier ut via så kontakt kundeservice. </p>
          </div>
          <!-- / Company Landlord -->

          <form>
            <div v-if="signup.landlord.type === 'private'" class="row mb-4">
              <!-- Private Landlord -->
              <div class="col" md="6" sm="12">
                <hl-Input
                  type="text"
                  label="Fødselsdato"
                  placeholder="dd.mm.åååå"
                  v-model="landlordDateOfBirth"
                  name="landlord.dateOfBirth"
                  v-mask="'##.##.####'"
                  v-validate
                  autocomplete="birthdate"
                  data-cy="birthdate"
                ></hl-Input>
              </div>
              <!-- / Private Landlord -->
            </div>

            <!-- Company Landlord -->
            <div v-else>
              <div class="row">
                <div class="col-12">
                  <hl-SearchCompanies
                    :companyName="signup.landlord.company.name"
                    @companySet="populateCompany"
                  ></hl-SearchCompanies>
                </div>
                <!-- / Company Landlord -->
              </div>

              <div class="row mt-0 mt-md-4">
                <div class="col-12 col-md-8">
                  <hl-Input
                    type="text"
                    :label="translationCompanyName"
                    readonly
                    v-model="signup.landlord.company.name"
                    name="landlord.company.name"
                    v-validate
                    data-cy="company-name-component"
                  ></hl-Input>
                </div>

                <div class="col-12 col-md-4">
                  <hl-Input
                    type="text"
                    label="Organisasjonsnummer"
                    disabled
                    name="landlord.company.orgNumber"
                    v-model="signup.landlord.company.orgNumber"
                    data-cy="organization-number-component"
                  ></hl-Input>
                </div>
              </div>
            </div>

            <div class="row mt-4 mb-4">
              <div class="col col-12">
                <hl-Input
                  type="text"
                  placeholder="Eks: Utleieveien 2"
                  label="Adresse"
                  v-model="signup.landlord.address.street"
                  name="landlord.address.street"
                  v-validate
                  data-cy="landlord-street-component"
                />
              </div>
            </div>
            <div class="row mb-5">
              <div class="col col-md-4 col-sm-12">
                <hl-Input
                  type="number"
                  placeholder="Eks: 0481"
                  label="Postnummer"
                  v-model="signup.landlord.address.postalCode"
                  name="landlord.address.postalCode"
                  @input="fetchPostalArea"
                  ref="postalCode"
                  v-validate
                  data-cy="landlord-postalCode-component"
                />
              </div>
              <div class="col-12 col-md-8 mt-4 mt-md-0">
                <hl-Input
                  type="text"
                  placeholder=""
                  label="Poststed"
                  v-model="signup.landlord.address.postalArea"
                  name="landlord.address.postalArea"
                  v-validate="'required'"
                  readonly
                  data-cy="landlord-postalArea-component"
                />
              </div>
            </div>
          </form>
        </div>
      </div>

      <hl-NextBackBottomBar
          buttonSize="lg"
          nextLink="object"
          nextText="neste"
          backLink="home"
          backText="tilbake"
      />
  </div>
</template>

<script>
import axios from 'axios';
import Input from '@/components/Input.vue';
import SearchCompanies from '@modules/husleie-framework/src/components/forms/inputs/search/SearchCompanies.vue';
import H1 from '@modules/husleie-framework/src/components/headings/H1.vue';
import NextBackBottomBar from '@modules/husleie-framework/src/components/navigation/NextBackBottomBar.vue';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class.ts';
import i18nCommon from '@modules/i18n/mixins/Common';
import hlValidateBeforeLeave from '@modules/husleie-framework/src/mixins/hlValidateBeforeLeave';
import * as moment from 'moment';
import {mask} from 'vue-the-mask';

export default {
  name: 'Utleier',
  mixins: [hlValidateBeforeLeave, i18nCommon],
  directives: { mask },
  components: {
    'hl-H1': H1,
    'hl-SearchCompanies': SearchCompanies,
    'hl-NextBackBottomBar': NextBackBottomBar,
    'hl-Input': Input,
  },
  data() {
    return {
      displayResults: true,
      landlordDateOfBirth: this.signup.landlord.dateOfBirth ? moment(this.signup.landlord.dateOfBirth, 'YYYY-MM-DD').format('DD.MM.YYYY') : '',
      postalAreaCancelSource: undefined,
    }
  },
  props: {
    signup: {
      type: Object,
      required: true,
    },
  },
  computed: {
    companyNameOrPlaceholder() {
      if(this.signup.landlord.company.name.length === 0) {
        return 'din virksomhet'
      } else {
        return this.signup.landlord.company.name
      }
    },
    translationCompanyName() {
      return this.translate('landlord.company name');
    },
  },
  methods: {
    fetchPostalArea(e) {
      if (this.postalAreaCancelSource) {
        this.postalAreaCancelSource.cancel();
        this.postalAreaCancelSource = undefined;
      }

      this.signup.landlord.address.postalArea = '';
      if (e && e.match(/^\d\d\d\d$/)) {
        this.postalAreaCancelSource = axios.CancelToken.source();
        axios.get('/api/poststed/'+e, { cancelToken: this.postalAreaCancelSource.token })
          .then((response) => {
            if (response.data.error) {
              return;
            }

            this.signup.landlord.address.postalArea = response.data.poststed;
          })
          .catch(function(error) {
            if (axios.isCancel(error)) {
              return;
            }

            LogOperations.error('API|fetchPostalArea', error);
          });
      }
    },

    populateCompany(c) {
      this.signup.landlord.company.name = c.navn;
      this.signup.landlord.company.orgNumber = c.organisasjonsnummer;

      if ('forretningsadresse' in c) {
          this.signup.landlord.address.street = c.forretningsadresse.adresse;
          this.signup.landlord.address.postalCode = c.forretningsadresse.postnummer;
          // Trigger a input event on the postalCode to make sure we look up the postalArea value
          this.$refs.postalCode.$emit('input', this.signup.landlord.address.postalCode);
      }

      this.$nextTick( () => {
        this.$validator.validateAll();
      })
    }
  },

  watch: {
    landlordDateOfBirth(newValue) {
      if (newValue.match(/^\d\d\.\d\d\.\d\d\d\d$/)) {
        this.signup.landlord.dateOfBirth = moment(newValue, 'DD.MM.YYYY').format('YYYY-MM-DD');
      }
    }
  },

  mounted() {
    /* Tracking GTM */
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event':'VirtualPageview',
      'virtualPageURL':'/signup/step2/landlordInformation',
      'virtualPageTitle': 'Signup | Step 2 | Landlord Information'
    });
  }
};
</script>

<style scoped lang="scss">
  .results-container {
    background: white;
    max-height: 300px;
    overflow: scroll;
    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
    border: 1px solid $gray-400;
    margin-bottom: 0;

    li {
      padding: 1rem;
      border-bottom: 1px solid $gray-200;
      color: $gray-600;
      cursor: pointer;
    }

    li:last-child { border-bottom: none }

    li:hover {background: $gray-100 }
  }
</style>
